import { useEffect, useState } from 'react';
import {
    ArrowClockwise,
    CheckCircle,
    ClipboardText,
    MagnifyingGlass,
    Package,
    Truck,
} from 'shared/icons';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { Id } from '../../components/ui/Mono';
import {
    CollectorEmployeeProvider,
    useCollectorEmployee,
} from '../../components/warehouse/CollectorEmployeeContext';
import CollectorBadgeIdentify from '../../components/warehouse/collector/CollectorBadgeIdentify';
import FreeQueryPanel from '../../components/warehouse/collector/FreeQueryPanel';
import IntakeFlow from '../../components/warehouse/collector/IntakeFlow';
import MovePalletFlow from '../../components/warehouse/collector/MovePalletFlow';
import ServiceOrderCollector from '../../components/warehouse/collector/ServiceOrderCollector';
import RepackCollectorFlow from '../../components/warehouse/repack/RepackCollectorFlow';
import { useRepackLabels } from '../../components/warehouse/repack/repackLabels';
import ShipmentLoadingFlow from '../../components/warehouse/shipping/ShipmentLoadingFlow';
import { useShippingLabels } from '../../components/warehouse/shipping/shippingLabels';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { NormalizeOperatorRoles, OperatorCan } from '../../utils/operatorRoles';

// Modos da tela depois que o operador se identifica. O M0 entregou só a
// identificação; o M4 acrescentou os dois trabalhos que existem SEM OS, o M5
// trouxe o trabalho normal do galpão — a Ordem de Serviço — e o M6 a BANCADA da
// fábrica, que é o trabalho de quem fica na máquina o turno inteiro.
const MODE_MENU = 'MENU';
const MODE_FREE_QUERY = 'FREE_QUERY';
const MODE_MOVE = 'MOVE';
// M10 — inventário inicial: bipar a etiqueta em branco do rolo e declarar o
// que tem dentro. É o que enche o galpão no primeiro dia.
const MODE_INTAKE = 'INTAKE';
const MODE_SERVICE_ORDERS = 'SERVICE_ORDERS';
const MODE_REPACK = 'REPACK';
// M8 — o carregamento do embarque: bipar cada pallet/volume do romaneio + a
// etiqueta da doca. É trabalho de chão como os outros; o FECHAMENTO (que baixa
// estoque e dispara o espelho do Bling) fica no console do Supervisor.
const MODE_SHIPPING = 'SHIPPING';

/**
 * Tela do coletor (web responsivo, dedo/scanner).
 *
 * M0: identificação do funcionário pelo ID do crachá.
 * M4: depois de identificado, CONSULTA LIVRE ("o que é isto que eu bipei?") e
 * MOVER PALLET (a cadeia de bipes guiada) — os dois trabalhos que existem sem
 * OS: perguntar e mover.
 * M5: ORDENS DE SERVIÇO, que é o trabalho PLANEJADO (§6.5, "tudo é OS") e por
 * isso encabeça o menu. As duas ações do M4 continuam inteiras ao lado dela: o
 * movimento avulso não é um atalho para a OS, é outro trabalho — o que aparece
 * quando o Supervisor pede algo no meio do turno.
 * M7: REPACK, que é mexer no CONTEÚDO do pallet (dividir/juntar) — trabalho do
 * chão como os outros, com uma regra que a tela precisa deixar clara: o pallet
 * de destino já tem que estar etiquetado, porque criar LPN é ato do Supervisor
 * (§6.2).
 *
 * Tudo dimensionado para dedo com luva — alvo grande, contraste alto, nada que
 * dependa de hover (o coletor é toque, não mouse).
 */
const CollectorScreen = ({ initialMode = MODE_MENU }) => {
    // `useRepackLabels` É o dicionário do módulo — só acrescenta o fallback das
    // chaves do M7 enquanto elas não entram em `warehouseLabels.js`. Um `label`
    // só para a tela inteira evita dois vocabulários no mesmo coletor.
    const { label } = useRepackLabels();
    // Idem para a expedição (M8): mesmo dicionário, fallback das chaves novas.
    const { label: shippingLabel } = useShippingLabels();
    const { user } = useAuth();
    const { employee, clear } = useCollectorEmployee();
    const [mode, setMode] = useState(initialMode);
    // Equipamento da sessão do coletor (opcional) — vai no movimento para o
    // motor atualizar `equipment.last_position_location_id` (§6.4).
    const [equipmentCode, setEquipmentCode] = useState('');
    const [equipmentList, setEquipmentList] = useState([]);

    // Lista de equipamentos ATIVOS, carregada uma vez por identificação.
    useEffect(() => {
        if (!employee) {
            return;
        }
        api.getWarehouseEquipment({ active: 'true', limit: 100 })
            .then((data) => setEquipmentList(Array.isArray(data?.results) ? data.results : []))
            .catch(() => setEquipmentList([]));
    }, [employee]);

    const handleLogout = () => {
        clear();
        setMode(MODE_MENU);
        setEquipmentCode('');
    };

    // Sair de um fluxo volta ao MENU — inclusive quando a página foi aberta
    // direto num modo (rota da bancada): quem terminou o trabalho da máquina
    // continua sendo um operador com o coletor na mão.
    const backToMenu = () => setMode(MODE_MENU);

    // Um fluxo do M4 só roda com operador identificado — perder a identificação
    // (trocar de operador) tem que devolver a tela ao menu, nunca deixar um
    // fluxo guiado órfão gravando movimento sem dono.
    const isFlowMode = Boolean(employee) && mode !== MODE_MENU;
    // Gate por papel do crachá: o terminal mostra só o que ESTE operador pode
    // fazer (§ papéis 25/08). `supervisor` faz tudo; consulta livre nunca é
    // gated. Só afeta o MENU — os fluxos já validam no backend.
    const operatorRoles = employee?.roles;
    const can = {
        serviceOrders: OperatorCan(operatorRoles, 'service_orders'),
        move: OperatorCan(operatorRoles, 'move'),
        intake: OperatorCan(operatorRoles, 'intake'),
        repack: OperatorCan(operatorRoles, 'repack'),
        shipping: OperatorCan(operatorRoles, 'shipping'),
    };

    return (
        <div className="mx-auto flex min-h-[70vh] max-w-md flex-col gap-6">
            {/* Header compacto */}
            <header className="flex items-center justify-between rounded-xl border border-mono-200 bg-white px-4 py-3 dark:border-mono-800 dark:bg-mono-950">
                <div className="flex items-center gap-2">
                    <h1 className="text-lg font-bold text-black dark:text-white">{label('wms.collector.title')}</h1>
                    <Badge>{label('wms.collector.subtitle')}</Badge>
                </div>
                <div className="text-right text-sm">
                    {employee ? (
                        <span className="font-medium text-black dark:text-white">{employee.employee_name}</span>
                    ) : (
                        <span className="text-mono-500">{label('wms.collector.not_identified')}</span>
                    )}
                </div>
            </header>

            {/* Área de ação central. Identificação e menu vivem num cartão
                centrado; os fluxos do M4 ocupam a coluna inteira — eles têm
                moldura própria (passo em tela cheia) e o cartão atrapalharia. */}
            <main className={isFlowMode ? 'flex-1' : 'flex flex-1 items-center'}>
                {isFlowMode && mode === MODE_SERVICE_ORDERS ? (
                    <ServiceOrderCollector label={label} operator={employee} onExit={backToMenu} />
                ) : isFlowMode && mode === MODE_REPACK ? (
                    <RepackCollectorFlow label={label} operator={employee} onExit={backToMenu} />
                ) : isFlowMode && mode === MODE_SHIPPING ? (
                    <ShipmentLoadingFlow label={shippingLabel} operator={employee} onExit={backToMenu} />
                ) : isFlowMode && mode === MODE_INTAKE ? (
                    <IntakeFlow label={label} operatorCode={employee?.employee_code} onExit={backToMenu} />
                ) : isFlowMode && mode === MODE_MOVE ? (
                    <MovePalletFlow
                        label={label}
                        operator={employee}
                        equipmentCode={equipmentCode}
                        onExit={backToMenu}
                    />
                ) : isFlowMode ? (
                    <FreeQueryPanel label={label} onExit={backToMenu} />
                ) : (
                <div className="w-full rounded-xl border border-mono-200 bg-white p-6 dark:border-mono-800 dark:bg-mono-950">
                    {employee ? (
                        <div className="space-y-6 text-center">
                            <CheckCircle size={56} className="mx-auto text-success-600 dark:text-success-400" />
                            <div className="space-y-2">
                                <p className="text-sm uppercase tracking-wide text-mono-500">
                                    {label('wms.collector.operator')}
                                </p>
                                <p className="text-3xl font-bold text-black dark:text-white">
                                    {employee.employee_name}
                                </p>
                                <div className="flex flex-wrap items-center justify-center gap-2">
                                    <Id value={employee.employee_code} className="text-lg" />
                                    {NormalizeOperatorRoles(employee.roles).map((r) => (
                                        <Badge key={r} variant={r === 'supervisor' ? 'info' : 'default'}>
                                            {label(`wms.operator_role.${r}`)}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Equipamento da SESSÃO: é ele que faz a "última
                                posição" andar (§6.4 — só endereço de RACK
                                conta). Opcional de propósito: movimento a pé
                                existe; sem equipamento, só não há o que
                                atualizar. */}
                            <div className="text-left">
                                <label
                                    htmlFor="collector-equipment"
                                    className="mb-1 block text-xs font-semibold uppercase tracking-wide text-mono-500"
                                >
                                    {label('wms.collector.equipment')}
                                </label>
                                <select
                                    id="collector-equipment"
                                    value={equipmentCode}
                                    onChange={(event) => setEquipmentCode(event.target.value)}
                                    className="w-full rounded-lg border-2 border-mono-300 bg-white px-3 py-3 text-base text-black dark:border-mono-700 dark:bg-mono-950 dark:text-white"
                                >
                                    <option value="">{label('wms.collector.equipment_none')}</option>
                                    {equipmentList.map((item) => (
                                        <option key={item.id} value={item.code}>
                                            {`${item.code} — ${label(`wms.equipment_type.${item.type}`)}`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* As ações do coletor. Botões altos e de largura
                                total: o alvo tem que aceitar dedo com luva, e a
                                escolha tem que ser óbvia de relance. Todas do
                                MESMO tamanho — só a cor diz qual é o trabalho
                                do dia (a OS); as outras duas seguem a um toque
                                de distância, com o mesmo alvo de sempre. */}
                            {NormalizeOperatorRoles(employee.roles).length === 0 ? (
                                <p className="rounded-lg bg-warning-50 px-4 py-3 text-sm text-warning-700 dark:bg-warning-950 dark:text-warning-300">
                                    {label('wms.collector.no_roles')}
                                </p>
                            ) : null}
                            <div className="space-y-3">
                                {can.serviceOrders && (
<Button
                                    size="lg"
                                    className="w-full py-6 text-xl font-black"
                                    onClick={() => setMode(MODE_SERVICE_ORDERS)}
                                >
                                    <ClipboardText size={26} className="mr-3" />
                                    {label('wms.collector.action_service_orders')}
                                </Button>
)}
                                {can.move && (
<Button
                                    variant="secondary"
                                    size="lg"
                                    className="w-full py-6 text-xl font-black"
                                    onClick={() => setMode(MODE_MOVE)}
                                >
                                    <Truck size={26} className="mr-3" />
                                    {label('wms.collector.action_move')}
                                </Button>
)}
                                {/* M10 — INVENTÁRIO: pegar a etiqueta em branco
                                    do rolo, bipar o produto, informar quanto e
                                    escolher a posição. É o fluxo que transforma
                                    papel em pallet. */}
                                {can.intake && (
<Button
                                    variant="secondary"
                                    size="lg"
                                    className="w-full py-6 text-xl font-black"
                                    onClick={() => setMode(MODE_INTAKE)}
                                >
                                    <Package size={26} className="mr-3" />
                                    {label('wms.intake.action')}
                                </Button>
)}
                                {/* M7 — REPACK do chão: dividir/juntar pallet
                                    bipando origem, destino e quantidade. O
                                    destino já tem que estar etiquetado: criar
                                    pallet é ato do Supervisor (§6.2). */}
                                {can.repack && (
<Button
                                    variant="secondary"
                                    size="lg"
                                    className="w-full py-6 text-xl font-black"
                                    onClick={() => setMode(MODE_REPACK)}
                                >
                                    <Package size={26} className="mr-3" />
                                    {label('wms.repack.collector.action')}
                                </Button>
)}
                                {/* "Carregar embarque" saiu (03/09): a operação não
                                    faz conferência de carga hoje. O romaneio do WMS
                                    antigo agora vira uma OS de SEPARAÇÃO PARA ENVIO,
                                    que aparece em Ordens de Serviço junto com as de
                                    picking — mesmo lugar, mesma execução. */}
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    className="w-full py-6 text-xl font-black"
                                    onClick={() => setMode(MODE_FREE_QUERY)}
                                >
                                    <MagnifyingGlass size={26} className="mr-3" />
                                    {label('wms.collector.action_free_query')}
                                </Button>
                            </div>

                            <Button variant="ghost" size="lg" className="w-full" onClick={handleLogout}>
                                <ArrowClockwise size={20} className="mr-2" />
                                {label('wms.collector.change_operator')}
                            </Button>
                        </div>
                    ) : (
                        <CollectorBadgeIdentify label={label} />
                    )}
                </div>
                )}
            </main>

            {/* Rodapé de status */}
            <footer className="flex items-center justify-between gap-3 rounded-xl border border-mono-200 bg-white px-4 py-2 text-xs text-mono-500 dark:border-mono-800 dark:bg-mono-950">
                <span className="truncate" title={user?.email || undefined}>
                    {label('wms.collector.device')}: {user?.email || '—'}
                </span>
                <span className="shrink-0">
                    {employee ? (
                        <>
                            {label('wms.collector.operator')}: <Id value={employee.employee_code} />
                        </>
                    ) : (
                        label('wms.collector.not_identified')
                    )}
                </span>
            </footer>
        </div>
    );
};

/**
 * `initialMode` abre o coletor direto num modo depois da identificação. Hoje
 * nenhuma rota o passa: a BANCADA (`/bancada` e `/warehouse/bench`) é outra
 * tela, WarehouseBenchStation — que reusa a MESMA identificação por crachá
 * (CollectorBadgeIdentify), para haver um lugar só a consertar quando o
 * crachá mudar.
 */
const WarehouseCollectorPage = ({ initialMode = MODE_MENU }) => (
    <CollectorEmployeeProvider>
        <CollectorScreen initialMode={initialMode} />
    </CollectorEmployeeProvider>
);

export default WarehouseCollectorPage;
